<template lang="pug">
component(:is="layoutComponent" :showLogo="true" :noPadding="true")
  .ticket-checkout-page(:class="{ 'is-empty-cart': isEmptyCart, 'has-checkout-footer': !isEmptyCart && !pageError }")
    .checkout-container(v-if="!pageError" :class="{ 'checkout-container--empty': isEmptyCart }")
      //- Empty Cart State
      .empty-cart-section(v-if="isEmptyCart")
        .empty-cart-icon
          img(src="@/assets/icons/shopping-cart.svg" alt="Empty cart")
        h2.empty-cart-title Your cart is empty
        p.empty-cart-text Add tickets or items to your cart to get started
        el-button(
          type="primary"
          size="large"
          @click="goBackFromCheckout"
        ).browse-btn {{ checkoutReturnLabel }}

      //- Order Summary (only show if cart has items)
      .checkout-section.order-summary-section(v-if="lines.length > 0")
        .section-header
          h2.section-title Order Summary
          button.clear-cart-btn(
            v-if="!hasPaymentIntent"
            type="button"
            @click="clearCart"
            title="Clear cart"
            aria-label="Clear cart"
          )
            img(src="@/assets/icons/trash.svg" alt="")
            span Clear cart

        //- Creator groups (display only; entire cart checks out in one payment)
        .creator-groups
          .creator-group(
            v-for="group in creatorGroups"
            :key="group.creatorId"
          )
            .creator-header
              .creator-info
                span.creator-name {{ group.creatorTitle }}
              .creator-header-actions(@click.stop)
                span.creator-item-count {{ getGroupItemCount(group) }} {{ getGroupItemCount(group) === 1 ? 'item' : 'items' }}
                button.clear-creator-btn(
                  v-if="!hasPaymentIntent"
                  type="button"
                  @click="clearCreatorGroup(group)"
                )
                  img(src="@/assets/icons/trash.svg" alt="")
                  span Clear

            //- Items in this group
            .creator-items
              .order-lines-table
                .order-lines-header
                  span.col-item ITEM
                  span.col-qty QTY
                  span.col-price PRICE
                  span.col-total TOTAL

                .order-line-row(
                  v-for="line in group.lines"
                  :key="line.id"
                )
                  .col-item
                    .line-title {{ getLineItemTitle(line) }}
                    .line-option(v-if="getLineItemSubtitle(line)") {{ getLineItemSubtitle(line) }}

                  .col-qty
                    .qty-cell
                      .qty-stepper(
                        v-if="canAdjustQuantity(line) && !hasPaymentIntent"
                        @click.stop
                      )
                        button.qty-btn(
                          type="button"
                          @click="decrementLineQuantity(line)"
                          :aria-label="`Decrease ${getLineItemTitle(line)} quantity`"
                        )
                          img(src="@/assets/icons/minus.svg" alt="")
                        span.qty-value {{ getLineQuantity(line) }}
                        button.qty-btn.plus(
                          type="button"
                          @click="incrementLineQuantity(line)"
                          :disabled="getLineQuantity(line) >= maxCartQuantity"
                          :aria-label="`Increase ${getLineItemTitle(line)} quantity`"
                        )
                          img(src="@/assets/icons/plus.svg" alt="")
                      span.qty-static(v-else-if="!line.isFree") {{ getLineQuantity(line) }}
                      span.qty-static.qty-static--muted(v-else) —

                  .col-price
                    span.price-free(v-if="line.isFree") Free
                    .price-input-wrap(
                      v-else-if="canEditLineUnitPrice(line) && !hasPaymentIntent"
                      @click.stop
                    )
                      span.price-prefix $
                      input.price-input(
                        type="text"
                        inputmode="decimal"
                        :value="getLinePriceDisplay(line)"
                        :aria-label="`Price for ${getLineItemTitle(line)}`"
                        @input="onLinePriceInput(line.id, $event)"
                        @blur="commitLinePrice(line)"
                      )
                    span.price-readonly(v-else) ${{ getLineUnitPrice(line).toFixed(2) }}

                  .col-total
                    span.line-total-text {{ formatLineTotal(line) }}
                    button.remove-btn(
                      v-if="!hasPaymentIntent"
                      type="button"
                      @click.stop="removeLine(line.id)"
                      title="Remove item"
                      aria-label="Remove item"
                    )
                      img(src="@/assets/icons/close.svg" alt="Remove")
                    span.remove-btn-spacer(v-else aria-hidden="true")

                .order-lines-footer
                  span.col-item
                  span.col-qty
                  span.col-price
                  .col-total
                    span.subtotal-label SUBTOTAL
                    span.subtotal-amount ${{ calculateGroupSubtotal(group).toFixed(2) }}
                    span.remove-btn-spacer(
                      v-if="!hasPaymentIntent"
                      aria-hidden="true"
                    )

      //- Simple order summary when cart was cleared after intent / during pay
      .checkout-section(v-else-if="(hasPaymentIntent || paymentMounted) && serverAmount !== null")
        h2.section-title Order Summary
        .fee-row.fee-total
          span Total Amount
          span ${{ (serverAmount / 100).toFixed(2) }}

      //- Contact Info (anonymous users only, not for payment intents with email)
      .checkout-section.contact-section(
        v-if="!isEmptyCart && !isAuthenticated && !hasPaymentIntent"
      )
        h2.section-title Contact Information
        el-form(
          ref="contactFormRef"
          :model="contactInfo"
          :rules="contactRules"
          label-position="top"
        )
          .form-row
            el-form-item(label="First Name" prop="firstName").form-col
              el-input(v-model="contactInfo.firstName" placeholder="First name")
            el-form-item(label="Last Name" prop="lastName").form-col
              el-input(v-model="contactInfo.lastName" placeholder="Last name")
          el-form-item(label="Email" prop="email")
            el-input(v-model="contactInfo.email" type="email" placeholder="your@email.com")
          el-form-item(label="Phone (optional)" prop="phone")
            el-input(
              v-model="contactInfo.phone"
              type="tel"
              placeholder="(123) 456-7890"
            )

      //- Giving Wallet
      .checkout-section.wallet-section(v-if="!isEmptyCart && isAuthenticated && hasWallet && walletEligibleTotal > 0")
        .wallet-toggle
          el-checkbox(v-model="useGivingWallet")
            | Use Giving Wallet credit (up to ${{ maxWalletCredit.toFixed(2) }})
          .wallet-info-text(v-if="walletEligibleTotal < subtotal")
            | Only donation portions are wallet-eligible
          .wallet-note(v-if="useGivingWallet")
            span Applied: ${{ Math.min(givingWalletAmount, maxWalletCredit).toFixed(2) }}
            span &nbsp;·&nbsp;
            span Remaining charge: ${{ chargeAfterWallet.toFixed(2) }}

      //- Fee Breakdown (only show if cart has items)
      .checkout-section(v-if="lines.length > 0")
        h2.section-title Price Breakdown
        .fee-row
          span Subtotal
          span ${{ subtotal.toFixed(2) }}
        .fee-row(v-if="subtotal > 0")
          span Platform fee (2%)
          span ${{ fees.convenienceFee.toFixed(2) }}
        .fee-row(v-if="subtotal > 0")
          span Credit card fee (2.9% + $0.30)
          span ${{ fees.creditCardFee.toFixed(2) }}
        .fee-row(v-if="useGivingWallet && givingWalletAmount > 0")
          span.text-success Giving Wallet credit
          span.text-success -${{ Math.min(givingWalletAmount, maxWalletCredit).toFixed(2) }}
        .fee-row.fee-total
          span Total
          span(v-if="isFreeCheckout") FREE
          span(v-else) ${{ checkoutTotalAmount.toFixed(2) }}

      //- Opt-in notifications
      .checkout-section.optin-section(v-if="!isEmptyCart")
        el-checkbox(v-model="optInNotifications")
          | Opt into marketing for updates

      //- Stripe Payment Element
      .checkout-section.payment-section(v-if="paymentMounted")
        h2.section-title Payment
        #payment-element

    //- Fixed bottom checkout actions
    .checkout-footer(v-if="!isEmptyCart && !pageError")
      .checkout-footer-inner
        .checkout-footer-actions
          el-button(
            v-if="!paymentMounted"
            type="primary"
            :loading="loading"
            :disabled="loading"
            @click="initiateCheckout"
            size="large"
          ).checkout-btn
            span {{ subtotal === 0 ? 'Get Free Tickets' : 'Proceed to Payment' }}

          el-button(
            v-if="paymentMounted"
            type="primary"
            :loading="loading"
            :disabled="loading || !paymentReady"
            @click="confirmPayment"
            size="large"
          ).checkout-btn
            span Pay ${{ serverAmount !== null ? (serverAmount / 100).toFixed(2) : '...' }}

          button.cancel-btn(type="button" @click="goBack") Cancel

    .page-error(v-if="pageError")
      p {{ pageError }}
      el-button(type="primary" @click="router.push('/web')") Back to Home
</template>

<script lang="ts">
import {
  computed, defineComponent, nextTick, onMounted, reactive, ref, watch,
} from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { ElLoading } from 'element-plus';
import WebAppLayout from '../dashboard/WebAppLayout.vue';
import { makeRequest } from '@/graphs/index';
import { createCartCheckoutIntent, benevityDetails, findMyTickets } from '@/graphs/gql';
import type { CartLine, CartCreatorGroup } from '@/store/modules/cart';
import globalShared from '@/shared';
import webAppToast from '@/utils/webAppToast';
import {
  confirmClearCart,
  confirmClearCreatorGroup,
  confirmRemoveCartItem,
} from '@/utils/cartConfirm';
import { clearCartAfterSuccessfulCheckout } from '@/utils/clearCartAfterCheckout';
import {
  appendEmbedOrganizationQuery,
  captureEmbedOrganizationContext,
} from '@/utils/embeddedUtils';
import {
  appendCheckoutReturnQuery,
  navigateCheckoutReturn,
  persistCheckoutReturnFromRoute,
  resolveCheckoutReturnLabel,
} from '@/utils/checkoutNavigation';
import {
  countAllExpectedTickets,
  formatCheckoutErrorMessage,
  getTicketStickerIds,
  saveGuestCheckoutContact,
  saveTicketCheckoutContext,
} from '@/utils/ticketDisplay';
import { ticketLog } from '@/utils/ticketLogger';
import { getCartLinesMissingRequiredCause } from '@/utils/cartItemAmount';
import {
  clearCheckoutIdempotencyKey,
  ensureCheckoutIdempotencyKey as resolveCheckoutIdempotencyKey,
  getStoredCheckoutIdempotencyKey,
} from '@/utils/checkoutIdempotency';

interface ContactInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

function calculateFinalPayment(subtotalAmt: number) {
  if (subtotalAmt === 0) return { convenienceFee: 0, creditCardFee: 0 };
  const convenienceFee = Math.min(parseFloat((0.02 * subtotalAmt).toFixed(2)), 30);
  const totalWithConvenience = subtotalAmt + convenienceFee;
  const total = parseFloat(((totalWithConvenience + 0.30) / (1 - 0.029)).toFixed(2));
  const creditCardFee = parseFloat((total - totalWithConvenience).toFixed(2));
  return { convenienceFee, creditCardFee };
}

function loadStripeScript(callback: () => void) {
  const existing = document.querySelector('script[src*="js.stripe.com"]');
  if (existing) {
    if ((window as any).Stripe) { callback(); } else { existing.addEventListener('load', callback); }
    return;
  }
  const script = document.createElement('script');
  script.src = 'https://js.stripe.com/v3/';
  script.addEventListener('load', callback);
  document.head.appendChild(script);
}

export default defineComponent({
  name: 'TicketCheckout',
  components: { WebAppLayout },
  setup() {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const { getMobileOperatingSystem, isSafari, handleSafariPrompt } = globalShared();

    // Mobile webview opens /payment/intent/cart/:cartId; web opens /web/checkout
    const isMobileAppCheckout = computed(() => {
      const isCartIntent = route.name === 'CartPaymentIntent';
      return isCartIntent;
    });

    // Check if this is a payment intent from backend (mobile flow)
    // For free checkouts, client_secret is optional
    const hasPubkey = !!route.query.pubkey;
    const hasClientSecret = !!route.query.client_secret;
    const isFree = route.query.free === 'true';
    const hasPaymentIntent = !!(route.query.pubkey && (route.query.client_secret || route.query.free === 'true'));
    const cartId = route.params.cartId as string;
    
    // Use plain div wrapper when payment intent (parent route provides layout)
    const layoutComponent = computed(() => {
      const component = hasPaymentIntent ? 'div' : 'web-app-layout';
      return component;
    });

    const lines = computed<CartLine[]>(() => store.getters['cart/cartLines']);
    const creatorGroups = computed<CartCreatorGroup[]>(() => store.getters['cart/creatorGroups']);
    const isConfirmingPayment = ref(false);
    const isEmptyCart = computed(() => (
      !hasPaymentIntent
      && lines.value.length === 0
      && !paymentMounted.value
      && !isConfirmingPayment.value
    ));

    const contactInfo = reactive<ContactInfo>({
      firstName: '', lastName: '', email: '', phone: '',
    });
    const contactFormRef = ref();
    const checkoutIdempotencyKey = ref<string | null>(getStoredCheckoutIdempotencyKey());

    const me = computed(() => {
      const user = store.getters['users/me'];
      return user;
    });
    const isAuthenticated = computed(() => {
      const auth = !!me.value;
      return auth;
    });

    watch(me, (user) => {
      if (user) {
        contactInfo.email = user.email || '';
        contactInfo.firstName = user.firstname || '';
        contactInfo.lastName = user.lastname || '';
      }
    }, { immediate: true });

    const contactRules = {
      firstName: [{ required: true, message: 'First name is required', trigger: 'blur' }],
      lastName: [{ required: true, message: 'Last name is required', trigger: 'blur' }],
      email: [
        { required: true, message: 'Email is required', trigger: 'blur' },
        { type: 'email', message: 'Enter a valid email', trigger: 'blur' },
      ],
    };

    const subtotal = computed(() => (
      lines.value.reduce((sum, line) => sum + calculateLineTotal(line), 0)
    ));

    const fees = computed(() => calculateFinalPayment(subtotal.value));

    const totalWithFees = computed(() => {
      if (subtotal.value === 0) return 0;
      return parseFloat(
        (subtotal.value + fees.value.convenienceFee + fees.value.creditCardFee).toFixed(2),
      );
    });

    const walletBalance = ref(0);
    const hasWallet = ref(false);
    const useGivingWallet = ref(false);

    const donationTotal = computed(() => (
      lines.value
        .filter((l) => l.lineType === 'Donation')
        .reduce((sum, l) => {
          const amount = l.donationAmount ?? 0;
          const causeCount = l.donationCauseIds?.length ?? 1;
          return sum + (amount * causeCount);
        }, 0)
    ));

    const walletEligibleTotal = computed(() => {
      let total = 0;

      for (const line of lines.value) {
        // Donation add-ons are always wallet-eligible
        if (line.lineType === 'Donation') {
          const amount = line.donationAmount ?? 0;
          const causeCount = line.donationCauseIds?.length ?? 1;
          total += amount * causeCount;
          continue;
        }

        // Campaign tickets (ticket lines with donationCauseId) are wallet-eligible
        if (line.lineType === 'Ticket' && line.donationCauseId) {
          const amount = (line.unitCost ?? 0) * (line.quantity ?? 1);
          total += amount;
          continue;
        }

        // Sticker lines: Campaign stickers are wallet-eligible, MatchCampaign are NOT
        if (line.lineType === 'Sticker') {
          const campaignType = line.campaignType?.toLowerCase();
          if (campaignType === 'campaign') {
            // Campaign stickers are wallet-eligible
            const amount = (line.unitCost ?? 0) * (line.quantity ?? 1);
            const causeCount = line.donationCauseIds?.length ?? 1;
            total += amount * causeCount;
          }
          // MatchCampaign and other sticker types are NOT wallet-eligible
          continue;
        }
      }

      return total;
    });

    const maxWalletCredit = computed(() => Math.min(walletBalance.value, walletEligibleTotal.value));
    const givingWalletAmount = computed(() => (useGivingWallet.value ? maxWalletCredit.value : 0));
    const chargeAfterWallet = computed(() => Math.max(0, totalWithFees.value - givingWalletAmount.value));

    const optInNotifications = ref(true);

    const serverAmount = ref<number | null>(null);
    const checkoutOrderId = ref('');
    const loading = ref(false);
    const stripeError = ref<string | null>(null);
    const pageError = ref<string | null>(null);
    const paymentMounted = ref(false);
    const paymentReady = ref(false);

    const checkoutTotalAmount = computed(() => {
      if (paymentMounted.value && serverAmount.value !== null) {
        return serverAmount.value / 100;
      }
      return totalWithFees.value;
    });

    const isFreeCheckout = computed(() => (
      subtotal.value === 0 && !(paymentMounted.value && serverAmount.value !== null)
    ));

    let stripe: any = null;
    let elements: any = null;

    async function fetchWalletBalance() {
      try {
        const response = await makeRequest('query', benevityDetails, {}, { token: me.value?.token });
        if (response?.data?.benevityDetails) {
          walletBalance.value = parseFloat(response.data.benevityDetails.amountInWallet || 0);
          hasWallet.value = walletBalance.value > 0;
        }
      } catch (e) {
        console.error('Wallet fetch error:', e);
      }
    }

    function getLineUnitPrice(line: CartLine): number {
      if (line.lineType === 'Donation') {
        return line.donationAmount ?? 0;
      }
      if (line.sweepstakesEntryCost != null) {
        return line.sweepstakesEntryCost;
      }
      return line.unitCost ?? 0;
    }

    function calculateLineTotal(line: CartLine): number {
      if (line.isFree) return 0;
      if (line.lineType === 'Ticket') {
        return (line.unitCost ?? 0) * (line.quantity ?? 1);
      }
      if (line.lineType === 'Donation') {
        const amount = line.donationAmount ?? 0;
        const causeCount = line.donationCauseIds?.length ?? 1;
        return amount * causeCount;
      }
      if (line.lineType === 'Sticker') {
        const amount = (line.unitCost ?? 0) * (line.quantity ?? 1);
        const causeCount = line.donationCauseIds?.length ?? 1;
        return amount * causeCount;
      }
      return line.unitCost ?? 0;
    }

    function formatLineTotal(line: CartLine): string {
      if (line.isFree) return 'Free';
      return `$${calculateLineTotal(line).toFixed(2)}`;
    }

    function getLineItemTitle(line: CartLine): string {
      if (line.lineType === 'Donation') return 'Donation';
      if (line.lineType === 'Ticket') return line.variantTitle || 'Ticket';
      return line.stickerTitle || line.variantTitle || line.lineType;
    }

    function getLineItemSubtitle(line: CartLine): string {
      if (line.lineType === 'Ticket' && line.selectedOption) {
        return `Option: ${line.selectedOption}`;
      }
      if (line.lineType === 'Donation' && line.donationCauseIds && line.donationCauseIds.length > 1) {
        return `${line.donationCauseIds.length} causes`;
      }
      if (line.lineType === 'Sticker' && line.donationCauseIds && line.donationCauseIds.length > 1) {
        return `${line.donationCauseIds.length} causes × $${getLineUnitPrice(line).toFixed(2)} each`;
      }
      if (line.lineType === 'Sticker' && line.sweepstakesEntryCost != null) {
        return 'Sweepstakes entry';
      }
      return '';
    }

    function canEditLineUnitPrice(line: CartLine): boolean {
      if (line.isFree) return false;
      if (line.lineType === 'Ticket') return false;
      return true;
    }

    const linePriceDrafts = ref<Record<string, string>>({});

    function getLinePriceDisplay(line: CartLine): string {
      const draft = linePriceDrafts.value[line.id];
      if (draft !== undefined) return draft;
      return getLineUnitPrice(line).toFixed(2);
    }

    function onLinePriceDraft(lineId: string, value: string) {
      linePriceDrafts.value = {
        ...linePriceDrafts.value,
        [lineId]: value,
      };
    }

    function onLinePriceInput(lineId: string, event: Event) {
      const target = event.target as HTMLInputElement | null;
      if (!target) return;
      onLinePriceDraft(lineId, target.value);
    }

    function applyLineUnitPrice(line: CartLine, price: number) {
      const updated: CartLine = { ...line };
      if (line.lineType === 'Donation') {
        updated.donationAmount = price;
      } else {
        updated.unitCost = price;
        if (line.sweepstakesEntryCost != null) {
          updated.sweepstakesEntryCost = price;
        }
        const campaignType = String(line.campaignType || '').toLowerCase();
        if (
          campaignType === 'campaign'
          || campaignType === 'matchcampaign'
          || line.donationCauseId
          || line.donationCauseIds?.length
          || line.donationAmount != null
        ) {
          updated.donationAmount = price;
        }
      }
      store.dispatch('cart/updateLine', updated);
    }

    function commitLinePrice(line: CartLine) {
      const draft = linePriceDrafts.value[line.id];
      const raw = draft !== undefined ? draft : getLineUnitPrice(line).toFixed(2);
      const cleaned = raw.replace(/[^0-9.]/g, '');
      const parsed = parseFloat(cleaned);

      const nextDrafts = { ...linePriceDrafts.value };
      delete nextDrafts[line.id];
      linePriceDrafts.value = nextDrafts;

      if (Number.isNaN(parsed) || parsed < 0) {
        return;
      }

      const rounded = Math.round(parsed * 100) / 100;
      if (rounded === getLineUnitPrice(line)) {
        return;
      }
      applyLineUnitPrice(line, rounded);
    }

    // Calculate subtotal for a creator group
    function calculateGroupSubtotal(group: CartCreatorGroup): number {
      return group.lines.reduce((sum, line) => sum + calculateLineTotal(line), 0);
    }

    function findScrollContainer(el: HTMLElement): HTMLElement | null {
      let parent = el.parentElement;
      while (parent && parent !== document.body) {
        const { overflowY } = window.getComputedStyle(parent);
        if (
          (overflowY === 'auto' || overflowY === 'scroll')
          && parent.scrollHeight > parent.clientHeight
        ) {
          return parent;
        }
        parent = parent.parentElement;
      }
      return null;
    }

    function scrollToCheckoutSection(target: 'contact' | 'order' | 'payment') {
      const selectorMap = {
        contact: '.contact-section',
        order: '.order-summary-section',
        payment: '.payment-section',
      };

      nextTick(() => {
        nextTick(() => {
          const root = document.querySelector('.ticket-checkout-page');
          const selector = selectorMap[target];
          const el = root?.querySelector(selector) as HTMLElement | null;
          if (!el) return;

          const rect = el.getBoundingClientRect();
          const scrollContainer = findScrollContainer(el);
          const offset = 16;

          if (scrollContainer) {
            const containerRect = scrollContainer.getBoundingClientRect();
            const nextTop = scrollContainer.scrollTop + rect.top - containerRect.top - offset;
            scrollContainer.scrollTo({ top: Math.max(0, nextTop), behavior: 'smooth' });
            return;
          }

          const nextTop = window.scrollY + rect.top - offset;
          window.scrollTo({ top: Math.max(0, nextTop), behavior: 'smooth' });
        });
      });
    }

    function resetPaymentState() {
      paymentMounted.value = false;
      paymentReady.value = false;
      serverAmount.value = null;
      checkoutOrderId.value = '';
      stripeError.value = null;
      stripe = null;
      elements = null;

      const paymentEl = document.getElementById('payment-element');
      if (paymentEl) paymentEl.innerHTML = '';
    }

    function clearIdempotencyKey() {
      checkoutIdempotencyKey.value = null;
      clearCheckoutIdempotencyKey();
    }

    function ensureCheckoutIdempotencyKey(): string {
      const key = resolveCheckoutIdempotencyKey(checkoutIdempotencyKey.value);
      checkoutIdempotencyKey.value = key;
      return key;
    }

    // Cart edits invalidate any in-progress payment intent
    watch(lines, () => {
      if (hasPaymentIntent) return;

      if (paymentMounted.value || serverAmount.value !== null) {
        resetPaymentState();
        clearIdempotencyKey();
      }
    }, { deep: true });

    function buildLines() {
      const builtLines = lines.value.map((line) => {
        const out: any = {
          stickerId: line.stickerId,
          lineType: line.lineType,
        };
        if (line.variantId) out.variantId = line.variantId;
        if (line.quantity) out.quantity = line.quantity;
        if (line.selectedOption) out.selectedOption = line.selectedOption;
        if (line.addOnId) out.addOnId = line.addOnId;

        if (line.sweepstakesEntries) out.sweepstakesEntries = line.sweepstakesEntries;
        
        // Multi-cause support: donationCauseIds takes precedence
        if (line.donationCauseIds && line.donationCauseIds.length > 0) {
          out.donationCauseIds = line.donationCauseIds;
          
          // For Sticker lines with multiple causes, backend needs donationAmount
          // This is the per-cause amount that backend will multiply by cause count
          if (line.lineType === 'Sticker' && line.unitCost) {
            out.donationAmount = line.unitCost * (line.quantity || 1);
          }
        } else if (line.donationCauseId) {
          out.donationCauseId = line.donationCauseId;

          // Single-cause campaign stickers need donationAmount for backend validation
          if (line.lineType === 'Sticker' && line.unitCost) {
            out.donationAmount = line.unitCost * (line.quantity || 1);
          }
        }
        
        if (line.donationAmount && !out.donationAmount) {
          out.donationAmount = line.donationAmount;
        }
        
        // V2 new fields from TICKET-1
        if (line.sweepstakesEntryCost) out.sweepstakesEntryCost = line.sweepstakesEntryCost;
        if (line.socialShopId) out.socialShopId = line.socialShopId;
        if (line.glowId) out.glowId = line.glowId;
        
        return out;
      });

      return builtLines;
    }

    function showCheckoutError(message: string) {
      webAppToast.error(formatCheckoutErrorMessage(message, lines.value));
    }

    async function loadStripeAndMount(pubKey: string, clientSecret: string) {
      return new Promise<void>((resolve, reject) => {
        loadStripeScript(async () => {
          try {
            stripe = (window as any).Stripe(pubKey);
            elements = stripe.elements({ clientSecret });
            const paymentElement = elements.create('payment');

            paymentMounted.value = true;
            await nextTick();

            paymentElement.mount('#payment-element');
            paymentElement.on('change', (e: any) => {
              paymentReady.value = e.complete;
              stripeError.value = e.error ? e.error.message : null;
            });
            resolve();
          } catch (err) {
            reject(err);
          }
        });
      });
    }

    async function validateGuestContact(): Promise<boolean> {
      if (isAuthenticated.value) return true;

      if (hasPaymentIntent) {
        const email = me.value?.email || contactInfo.email;
        if (!email) {
          webAppToast.warning('A valid email is required to link your purchase to your account.');
          return false;
        }
        return true;
      }

      try {
        await contactFormRef.value?.validate();
      } catch {
        webAppToast.warning('Please complete the contact information.');
        scrollToCheckoutSection('contact');
        return false;
      }

      return true;
    }

    async function initiateCheckout() {
      // If payment intent already provided (backend created it), skip to payment
      if (hasPaymentIntent) {
        if (!(await validateGuestContact())) return;
        // Payment form already ready, user can proceed to confirmPayment
        return;
      }

      if (!(await validateGuestContact())) return;

      if (!isAuthenticated.value && contactInfo.email) {
        saveGuestCheckoutContact(contactInfo.email, contactInfo.phone || '');
      }

      if (lines.value.length === 0) {
        webAppToast.error('No items to checkout.');
        scrollToCheckoutSection('order');
        return;
      }

      const missingCauseLines = getCartLinesMissingRequiredCause(lines.value);
      if (missingCauseLines.length) {
        const firstMissing = missingCauseLines[0];
        showCheckoutError(
          `'${firstMissing.stickerId}' is a donation campaign and requires a cause.`,
        );
        scrollToCheckoutSection('order');
        return;
      }

      loading.value = true;
      try {
        const builtLines = buildLines();
        const input: any = {
          email: me.value?.email || contactInfo.email || '',
          lines: builtLines,
          convenienceFee: fees.value.convenienceFee,
          creditCardFee: fees.value.creditCardFee,
          optInNotifications: optInNotifications.value,
          useGivingWallet: useGivingWallet.value,
          givingWalletAmount: useGivingWallet.value ? givingWalletAmount.value : 0,
          idempotencyKey: ensureCheckoutIdempotencyKey(),
        };

        if (contactInfo.firstName) input.firstName = contactInfo.firstName;
        if (contactInfo.lastName) input.lastName = contactInfo.lastName;
        if (contactInfo.phone) input.phone = contactInfo.phone.replace(/[^\d]/g, '');
        if (me.value) {
          input.purchaseBy = 'User';
          input.userId = me.value.id;
        }

        const token = me.value?.token || null;

        const response = await makeRequest(
          'mutation',
          createCartCheckoutIntent,
          { input },
          token ? { token } : null,
        );

        if (response?.errors) {
          throw new Error(response.errors[0]?.message);
        }

        const raw = response.data.createCartCheckoutIntent;
        const decoded = JSON.parse(atob(raw));

        serverAmount.value = decoded.amount;

        const rawOrderId = decoded.checkout_order_id;
        if (typeof rawOrderId === 'string') {
          checkoutOrderId.value = rawOrderId;
        } else if (rawOrderId && typeof rawOrderId === 'object') {
          checkoutOrderId.value = rawOrderId.id || rawOrderId.orderId || rawOrderId.order_id || '';
        } else {
          checkoutOrderId.value = '';
        }

        if (decoded.free === true) {
          const stickerId = lines.value[0]?.stickerId || '';
          clearIdempotencyKey();
          completeCheckout({ stickerId, isFree: true });
          return;
        }

        await loadStripeAndMount(decoded.stripe_pub_key, decoded.client_secret);
        scrollToCheckoutSection('payment');
      } catch (err: any) {
        const errorMsg = err.message || '';

        if (errorMsg.toLowerCase().includes('stripe account')
            || (errorMsg.toLowerCase().includes('organizer')
              && errorMsg.toLowerCase().includes('connected'))) {
          showCheckoutError(errorMsg);
        } else if (errorMsg.toLowerCase().includes('remaining')
                 || errorMsg.toLowerCase().includes('sold out')
                 || (errorMsg.toLowerCase().includes('ticket')
                   && errorMsg.toLowerCase().includes('available'))) {
          showCheckoutError(errorMsg);
        } else {
          showCheckoutError(errorMsg || 'Checkout failed. Please try again.');
        }
      } finally {
        loading.value = false;
      }
    }

    async function prepareCheckoutSuccess(stickerId: string) {
      const checkoutLines = lines.value;
      const ticketLines = checkoutLines.filter((line) => line.lineType === 'Ticket');
      const stickerIds = getTicketStickerIds(checkoutLines);
      const primaryStickerId = stickerId || stickerIds[0] || '';
      const expectedCount = countAllExpectedTickets(checkoutLines);

      ticketLog('Preparing checkout success context', {
        primaryStickerId,
        stickerIds,
        expectedCount,
        checkoutLineCount: checkoutLines.length,
        ticketLineCount: ticketLines.length,
        ticketLines: ticketLines.map((line) => ({
          stickerId: line.stickerId,
          variantId: line.variantId,
          variantTitle: line.variantTitle,
          quantity: line.quantity,
        })),
        orderId: checkoutOrderId.value,
      });

      let existingTicketIds: string[] = [];
      if (me.value?.token && stickerIds.length) {
        for (const sid of stickerIds) {
          try {
            const response = await makeRequest(
              'query',
              findMyTickets,
              { stickerId: sid },
              { token: me.value.token },
            );
            const ids = (response?.data?.findMyTickets || []).map(
              (ticket: { id: string }) => ticket.id,
            );
            existingTicketIds.push(...ids);
            ticketLog('Fetched existing tickets before checkout', { stickerId: sid, count: ids.length, ids });
          } catch (err) {
            ticketLog('Failed to fetch existing tickets before checkout', { stickerId: sid, err });
          }
        }
        existingTicketIds = [...new Set(existingTicketIds)];
      }

      const guestEmail = me.value?.email || contactInfo.email || '';
      const guestPhone = contactInfo.phone || '';
      const embedContext = captureEmbedOrganizationContext(route);

      if (!isAuthenticated.value && guestEmail) {
        saveGuestCheckoutContact(guestEmail, guestPhone);
      }

      saveTicketCheckoutContext({
        stickerId: primaryStickerId,
        stickerIds,
        orderId: checkoutOrderId.value,
        expectedCount,
        existingTicketIds,
        email: guestEmail || undefined,
        phone: guestPhone || undefined,
        embedOrganizationId: embedContext.organizationId || undefined,
        embedSource: embedContext.embedSource || undefined,
        cartLineSummary: checkoutLines.map((line) => ({
          lineType: line.lineType,
          stickerId: line.stickerId,
          stickerTitle: line.stickerTitle,
          variantId: line.variantId,
          variantTitle: line.variantTitle,
          quantity: line.quantity,
          unitCost: line.unitCost,
          donationAmount: line.donationAmount,
          isFree: line.isFree,
          donationCauseId: line.donationCauseId,
          donationCauseIds: line.donationCauseIds,
          sweepstakesEntries: line.sweepstakesEntries,
          sweepstakesEntryCost: line.sweepstakesEntryCost,
          campaignType: line.campaignType,
          selectedOption: line.selectedOption,
        })),
      });

      return expectedCount;
    }

    async function confirmPayment() {
      if (!stripe || !elements) return;
      stripeError.value = null;
      loading.value = true;
      isConfirmingPayment.value = true;

      try {
        const baseUrl = import.meta.env.VITE_WEB_BASE_URL || window.location.origin;
        const stickerId = lines.value[0]?.stickerId
          || (route.query.stickerId as string)
          || '';
        const expectedCount = await prepareCheckoutSuccess(stickerId);
        const stickerParam = stickerId ? `&stickerId=${stickerId}` : '';
        const ticketCountParam = expectedCount > 0 ? `&ticketCount=${expectedCount}` : '';
        const successBase = `${baseUrl}/web/ticket/success?orderId=${checkoutOrderId.value}${stickerParam}${ticketCountParam}`;
        const returnUrl = appendCheckoutReturnQuery(
          appendEmbedOrganizationQuery(successBase, route),
          route,
        );

        const { error: stripeErr } = await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: returnUrl,
            receipt_email: me.value?.email || contactInfo.email || undefined,
          },
          redirect: 'if_required',
        });

        if (stripeErr) {
          showCheckoutError(stripeErr.message || 'Payment failed. Please try again.');
          scrollToCheckoutSection('payment');
          return;
        }

        clearIdempotencyKey();

        if (isMobileAppCheckout.value) {
          clearCartAfterSuccessfulCheckout(store);
          const amount = serverAmount.value !== null
            ? serverAmount.value / 100
            : totalWithFees.value;
          redirectMobileApp(false, { stickerId, amount });
          return;
        }

        await redirectToWebSuccess({ stickerId, expectedCount });
      } finally {
        loading.value = false;
        isConfirmingPayment.value = false;
      }
    }

    async function redirectToWebSuccess({
      stickerId,
      isFree = false,
      expectedCount = 0,
    }: {
      stickerId: string;
      isFree?: boolean;
      expectedCount?: number;
    }) {
      clearCartAfterSuccessfulCheckout(store);

      const stickerParam = stickerId ? `&stickerId=${stickerId}` : '';
      const freeParam = isFree ? '&free=true' : '';
      const ticketCountParam = expectedCount > 0 ? `&ticketCount=${expectedCount}` : '';
      const successPath = appendCheckoutReturnQuery(
        appendEmbedOrganizationQuery(
          `/web/ticket/success?orderId=${checkoutOrderId.value}${stickerParam}${freeParam}${ticketCountParam}`,
          route,
        ),
        route,
      );
      const baseUrl = import.meta.env.VITE_WEB_BASE_URL || window.location.origin;

      if (!isAuthenticated.value) {
        saveGuestCheckoutContact(contactInfo.email, contactInfo.phone || '');
        const completeProfilePath = appendEmbedOrganizationQuery(
          `/auth/web/complete-profile?email=${encodeURIComponent(contactInfo.email)}`
          + `&redirect=${encodeURIComponent(successPath)}`,
          route,
        );
        window.location.href = `${baseUrl}${completeProfilePath.startsWith('/') ? '' : '/'}${completeProfilePath}`;
        return;
      }

      router.push(successPath);
    }

    async function completeCheckout({
      stickerId,
      isFree = false,
      amount,
    }: {
      stickerId: string;
      isFree?: boolean;
      amount?: number;
    }) {
      const expectedCount = await prepareCheckoutSuccess(stickerId);

      if (isMobileAppCheckout.value) {
        clearCartAfterSuccessfulCheckout(store);
        redirectMobileApp(false, {
          stickerId,
          amount: amount ?? (serverAmount.value !== null ? serverAmount.value / 100 : totalWithFees.value),
        });
        return;
      }

      await redirectToWebSuccess({ stickerId, isFree, expectedCount });
    }

    function getRedirectIOSUrl() {
      return import.meta.env.VITE_APP_REDIRECT_URL;
    }

    async function redirectMobileApp(
      failed = false,
      context?: { stickerId?: string; amount?: number },
    ) {
      const loadingInstance = ElLoading.service({ lock: false });
      const isAndroid = getMobileOperatingSystem() === 'Android';
      const appId = import.meta.env.VITE_APP_ID;
      const appHost = import.meta.env.VITE_APP_HOST;
      const browserFallbackUrlBase = import.meta.env.VITE_WEB_BASE_URL;
      const browserFallbackUrlPath = 'web/dashboard';

      const stickerId = context?.stickerId
        ?? lines.value[0]?.stickerId
        ?? (route.query.stickerId as string)
        ?? '';
      const amount = (context?.amount ?? (
        serverAmount.value !== null ? serverAmount.value / 100 : totalWithFees.value
      )).toFixed(2);
      const orderId = checkoutOrderId.value;
      const success = failed ? 'false' : 'true';

      if (isAndroid) {
        const encodedBrowserFallbackUrl = encodeURIComponent(
          `${browserFallbackUrlBase}/${browserFallbackUrlPath}`,
        );
        let intentUrl = `intent://${appHost}/?action=purchaseTickets&orderId=${orderId}&stickerId=${stickerId}&amount=${amount}&success=${success}`;
        intentUrl += `#Intent;scheme=${appId};package=${appId};S.browser_fallback_url=${encodedBrowserFallbackUrl};end`;
        window.location.href = intentUrl;
      } else {
        // iOS
        window.location.href = `${getRedirectIOSUrl()}?action=purchaseTickets&orderId=${orderId}&stickerId=${stickerId}&amount=${amount}&success=${success}`;
      }

      // Safari redirect handling
      let didSafariRedirect = false;
      if (isSafari()) {
        didSafariRedirect = await handleSafariPrompt();
      }

      // Check if redirect was successful
      if (!isSafari() || didSafariRedirect !== false) {
        setTimeout(() => {
          if (document.hidden) {
            window.location.href = `${browserFallbackUrlBase}/${browserFallbackUrlPath}`;
            loadingInstance.close();
          } else {
            loadingInstance.close();
          }
        }, 1500);
      } else {
        loadingInstance.close();
      }
    }

    const checkoutReturnLabel = computed(() => resolveCheckoutReturnLabel(route));

    function goBackFromCheckout() {
      navigateCheckoutReturn(router, route);
    }

    function goBack() {
      goBackFromCheckout();
    }

    async function clearCart() {
      const confirmed = await confirmClearCart();
      if (!confirmed) return;

      store.dispatch('cart/clearCart');
      clearIdempotencyKey();
      resetPaymentState();

      webAppToast.success('Cart cleared');
    }

    async function clearCreatorGroup(group: CartCreatorGroup) {
      const confirmed = await confirmClearCreatorGroup(group.creatorTitle);
      if (!confirmed) return;

      store.dispatch('cart/clearCreatorLines', group.creatorId);

      webAppToast.info(`Removed ${group.creatorTitle} items from cart`);
    }

    function removeLine(lineId: string) {
      store.dispatch('cart/removeLine', lineId);

      webAppToast.info('Item removed from cart');
    }

    const maxCartQuantity = 99;

    function isDonationStyleStickerLine(line: CartLine): boolean {
      if (line.lineType !== 'Sticker') return false;
      const campaignType = String(line.campaignType || '').toLowerCase();
      if (campaignType === 'campaign' || campaignType === 'matchcampaign') return true;
      if (campaignType === 'sweepstakes' || line.sweepstakesEntryCost != null) return true;
      if (line.donationAmount != null) return true;
      if (line.donationCauseId || line.donationCauseIds?.length) return true;
      return false;
    }

    function canAdjustQuantity(line: CartLine): boolean {
      if (line.lineType === 'Ticket') return true;
      if (line.lineType === 'Sticker' && !isDonationStyleStickerLine(line)) return true;
      return false;
    }

    function getLineQuantity(line: CartLine): number {
      return line.quantity ?? 1;
    }

    function incrementLineQuantity(line: CartLine) {
      const current = getLineQuantity(line);
      if (current >= maxCartQuantity) return;
      store.dispatch('cart/updateLine', { ...line, quantity: current + 1 });
    }

    async function decrementLineQuantity(line: CartLine) {
      const current = getLineQuantity(line);
      if (current <= 1) {
        const confirmed = await confirmRemoveCartItem(getLineItemTitle(line));
        if (!confirmed) return;
        removeLine(line.id);
        return;
      }
      store.dispatch('cart/updateLine', { ...line, quantity: current - 1 });
    }

    function getGroupItemCount(group: CartCreatorGroup): number {
      return group.lines.reduce((sum, line) => sum + (line.quantity ?? 1), 0);
    }

    onMounted(async () => {
      captureEmbedOrganizationContext(route);
      persistCheckoutReturnFromRoute(route);

      if (hasPaymentIntent) {
        const pubkey = route.query.pubkey as string;
        const clientSecret = route.query.client_secret as string;
        const amountCents = parseInt(route.query.amount as string, 10);

        serverAmount.value = amountCents;
        checkoutOrderId.value = cartId || '';

        if (route.query.email) {
          contactInfo.email = decodeURIComponent(route.query.email as string);
          saveGuestCheckoutContact(contactInfo.email, contactInfo.phone || '');
        }

        if (isFree) {
          paymentMounted.value = false;
        } else {
          try {
            await loadStripeAndMount(pubkey, clientSecret);
          } catch (err) {
            console.error('[Checkout] Failed to load Stripe:', err);
            pageError.value = 'Failed to load payment form. Please try again.';
          }
        }
      } else if (!isEmptyCart.value && me.value?.token) {
        await fetchWalletBalance();
      }
    });

    return {
      layoutComponent,
      lines,
      creatorGroups,
      contactInfo,
      contactRules,
      contactFormRef,
      isAuthenticated,
      hasPaymentIntent,
      isEmptyCart,
      subtotal,
      fees,
      totalWithFees,
      checkoutTotalAmount,
      isFreeCheckout,
      hasWallet,
      useGivingWallet,
      donationTotal,
      walletEligibleTotal,
      maxWalletCredit,
      givingWalletAmount,
      chargeAfterWallet,
      optInNotifications,
      serverAmount,
      loading,
      pageError,
      paymentMounted,
      paymentReady,
      initiateCheckout,
      confirmPayment,
      goBack,
      goBackFromCheckout,
      checkoutReturnLabel,
      clearCart,
      clearCreatorGroup,
      removeLine,
      canAdjustQuantity,
      canEditLineUnitPrice,
      getLineQuantity,
      getLineItemTitle,
      getLineItemSubtitle,
      getLineUnitPrice,
      getLinePriceDisplay,
      onLinePriceInput,
      commitLinePrice,
      formatLineTotal,
      incrementLineQuantity,
      decrementLineQuantity,
      getGroupItemCount,
      maxCartQuantity,
      router,
      calculateGroupSubtotal,
    };
  },
});
</script>

<style lang="scss" scoped>
.ticket-checkout-page {
  min-height: 100vh;
  background: #f5f5f7;
  padding: 1.5rem 1rem 4rem;
  box-sizing: border-box;

  @media (min-width: 1024px) {
    padding-left: 64px;
    padding-right: 64px;
  }

  &.has-checkout-footer {
    padding-bottom: 9.5rem;
  }

  &.is-empty-cart {
    display: flex;
    flex-direction: column;
    min-height: calc(100vh - 5rem);
    padding: 1rem;
  }
}

.checkout-container {
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
  box-sizing: border-box;

  @media (min-width: 1024px) {
    max-width: 1100px;
    margin-left: 0;
    margin-right: auto;
  }

  &--empty {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
  }
}

.checkout-section {
  width: 100%;
  max-width: 560px;
  margin-left: auto;
  margin-right: auto;
  background: #fff;
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1rem;
  box-sizing: border-box;
}

.contact-section,
.order-summary-section,
.payment-section {
  scroll-margin-top: 1rem;
  scroll-margin-bottom: 10rem;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.section-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 1rem;
}

.section-header .section-title {
  margin: 0;
}

.clear-cart-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.65rem;
  border: 1px solid #f5c6cb;
  border-radius: 6px;
  background: #fff5f5;
  color: #c0392b;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s, border-color 0.2s;

  img {
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
  }

  &:hover {
    background: #ffe8e8;
    border-color: #f1aeb5;
  }
}

.order-lines-table {
  width: 100%;
}

.order-lines-header,
.order-line-row,
.order-lines-footer {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 4.75rem 4.5rem 5.25rem;
  column-gap: 0.35rem;
  align-items: center;

  @media (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr) 4rem 4rem 4.75rem;
    column-gap: 0.25rem;
  }
}

.order-lines-header {
  padding: 0.35rem 0 0.65rem;
  border-bottom: 1px solid #eee;
  margin-bottom: 0.25rem;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #888;

  .col-item {
    text-align: left;
  }

  .col-qty {
    text-align: center;
  }

  .col-price {
    text-align: right;
  }

  .col-total {
    text-align: right;
    padding-right: 1.35rem;
  }
}

.order-line-row {
  padding: 0.65rem 0;
  border-bottom: 1px solid #f5f5f5;

  &:last-child {
    border-bottom: none;
  }
}

.col-item {
  min-width: 0;

  .line-title {
    font-size: 0.95rem;
    font-weight: 600;
    color: #1a1a1a;
    line-height: 1.3;
  }

  .line-option {
    font-size: 0.78rem;
    color: #888;
    margin-top: 0.15rem;
  }
}

.col-qty {
  display: flex;
  justify-content: center;
  align-items: center;
}

.qty-cell {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.qty-static {
  display: block;
  width: 100%;
  font-size: 0.95rem;
  font-weight: 600;
  color: #1a1a1a;
  text-align: center;
  font-variant-numeric: tabular-nums;

  &--muted {
    color: #bbb;
  }
}

.col-price {
  display: flex;
  justify-content: flex-end;
}

.price-free,
.price-readonly {
  font-size: 0.85rem;
  font-weight: 600;
  color: #1a1a1a;
  text-align: right;
}

.price-input-wrap {
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 4.25rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  padding: 0 0.4rem;
  min-height: 1.85rem;

  .price-prefix {
    color: #2563eb;
    font-weight: 700;
    font-size: 0.82rem;
    flex-shrink: 0;
    line-height: 1;
  }

  .price-input {
    width: 100%;
    min-width: 0;
    border: none;
    outline: none;
    font-size: 0.82rem;
    font-weight: 600;
    color: #1a1a1a;
    background: transparent;
    padding: 0.2rem 0 0.2rem 0.15rem;
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
}

.col-total {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.25rem;
  min-width: 0;

  .line-total-text {
    font-size: 0.9rem;
    font-weight: 700;
    color: #1a1a1a;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  .subtotal-label {
    font-size: 0.7rem;
    font-weight: 700;
    color: #888;
    letter-spacing: 0.04em;
    white-space: nowrap;
    margin-right: 0.15rem;
  }

  .subtotal-amount {
    font-size: 1rem;
    font-weight: 700;
    color: #1a1a1a;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  .remove-btn,
  .remove-btn-spacer {
    flex-shrink: 0;
    width: 1.25rem;
    height: 1.25rem;
  }

  .remove-btn {
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: background 0.2s;

    &:hover {
      background: rgba(255, 0, 0, 0.08);
    }

    img {
      width: 13px;
      height: 13px;
      opacity: 0.55;
      filter: invert(27%) sepia(85%) saturate(2450%) hue-rotate(346deg) brightness(95%);
    }
  }
}

.order-lines-footer {
  margin: 0.35rem -1rem -1rem;
  padding: 0.65rem 1rem 0.75rem;
  border-top: 1px solid #e5e5e5;
  background: #fafafa;
}

.qty-stepper {
  display: flex;
  align-items: center;
  gap: 0.2rem;
  flex-shrink: 0;
}

.qty-btn {
  width: 1.35rem;
  height: 1.35rem;
  border-radius: 9999px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: #f3f4f6;
  transition: background 0.2s;
  padding: 0;

  img {
    width: 0.6rem;
    height: 0.6rem;
  }

  &.plus {
    background: #1fbeaf;

    img {
      filter: brightness(0) invert(1);
    }

    &:hover:not(:disabled) {
      background: rgba(31, 190, 175, 0.9);
    }
  }

  &:hover:not(:disabled) {
    background: #e5e7eb;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.qty-value {
  font-size: 0.85rem;
  font-weight: 600;
  min-width: 0.85rem;
  text-align: center;
  color: #1a1a1a;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}

.form-row {
  display: flex;
  gap: 1rem;

  .form-col {
    flex: 1;
  }
}

.wallet-section {
  .wallet-toggle {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .wallet-note {
    font-size: 0.85rem;
    color: #555;
    padding-left: 1.5rem;
  }

  .wallet-info-text {
    font-size: 0.8rem;
    color: #666;
    margin-top: 0.5rem;
    padding-left: 1.5rem;
  }
}

.fee-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.35rem 0;
  font-size: 0.9rem;
  color: #444;
  border-bottom: 1px solid #f5f5f5;

  &:last-child {
    border-bottom: none;
  }

  &.fee-total {
    font-size: 1rem;
    font-weight: 700;
    color: #1a1a1a;
    padding-top: 0.65rem;
    margin-top: 0.25rem;
    border-top: 2px solid #e5e5e5;
    border-bottom: none;
  }
}

.checkout-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  border-top: 1px solid #e5e7eb;
  padding: 1rem 1rem 0.75rem;
  z-index: 40;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.08);

  @media (min-width: 1024px) {
    left: 300px;
    width: calc(100% - 300px);
  }
}

.checkout-footer-inner {
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
  display: flex;
  justify-content: center;

  @media (min-width: 1024px) {
    max-width: 1100px;
    margin-left: 0;
    margin-right: auto;
  }
}

.checkout-footer-actions {
  width: 100%;
  max-width: 360px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.65rem;

  :deep(.checkout-btn.el-button) {
    width: 100%;
    height: 52px;
    margin: 0;
    padding-left: 1.5rem;
    padding-right: 1.5rem;
    font-size: 1rem;
    font-weight: 700;
    border-radius: 9999px;
    border: none;
    background: linear-gradient(90deg, #0d9488 0%, #14b8a6 100%) !important;
    box-shadow: 0 8px 24px rgba(13, 148, 136, 0.3);

    .el-button__content,
    > span {
      display: inline-flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      text-align: center;
    }

    &:hover:not(:disabled) {
      background: linear-gradient(90deg, #0f766e 0%, #0d9488 100%) !important;
      box-shadow: 0 10px 28px rgba(13, 148, 136, 0.35);
    }

    &.is-loading {
      .el-icon {
        position: absolute;
        left: 1.25rem;
      }
    }
  }
}

.cancel-btn {
  width: 100%;
  border: none;
  background: transparent;
  color: #888;
  font-size: 0.9rem;
  padding: 0.15rem 0 0;
  cursor: pointer;
  text-align: center;
}

.optin-section {
  padding-top: 1rem;
  padding-bottom: 1rem;
}

.page-error {
  text-align: center;
  padding: 3rem 1rem;
  color: #c0392b;
}

#payment-element {
  margin-top: 0.5rem;
  min-height: 200px;
}

.creator-groups {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.creator-group {
  border: 2px solid #e5e5e5;
  border-radius: 12px;
  overflow: hidden;
}

.creator-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background: #fafafa;
  border-bottom: 1px solid #e5e5e5;
}

.creator-info {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  min-width: 0;

  .creator-name {
    font-size: 0.9rem;
    font-weight: 600;
    color: #1a1a1a;
  }
}

.creator-header-actions {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-shrink: 0;
}

.creator-item-count {
  font-size: 0.8rem;
  color: #888;
  font-weight: 500;
  white-space: nowrap;
}

.clear-creator-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border: 1px solid #f5c6cb;
  border-radius: 6px;
  background: #fff5f5;
  color: #c0392b;
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s, border-color 0.2s;

  img {
    width: 0.85rem;
    height: 0.85rem;
    flex-shrink: 0;
  }

  &:hover {
    background: #ffe8e8;
    border-color: #f1aeb5;
  }
}

.creator-items {
  padding: 1rem;
  background: white;
}

.empty-cart-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: #fff;
  border-radius: 12px;
  padding: 3rem 2rem;
  width: 100%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);

  .empty-cart-icon img {
    width: 4rem;
    height: 4rem;
    opacity: 0.35;
    margin-bottom: 1.25rem;
  }

  .empty-cart-title {
    font-size: 1.25rem;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0 0 0.5rem;
  }

  .empty-cart-text {
    font-size: 0.95rem;
    color: #888;
    margin: 0 0 1.5rem;
    max-width: 280px;
  }

  .browse-btn {
    min-width: 200px;
  }
}
</style>
